/**
 * Реестр раскладок и поиск клавиши по символу.
 *
 * Индексы строятся один раз на раскладку: поиск символа выполняется на каждом
 * нажатии, линейный обход по всем клавишам недопустим (NFR-P-02).
 */

import type { Finger, LayoutId, OsPlatform } from '@type2x/shared-contracts';
import { DEFAULT_PLATFORM } from '@type2x/shared-contracts';

import { EN_QWERTY } from './en-qwerty.js';
import { RU_JCUKEN, RU_JCUKEN_OVERRIDES } from './ru-jcuken.js';
import type {
  KeyDefinition,
  KeyOverride,
  KeyStroke,
  KeyboardLayout,
  PlatformOverrides,
} from './types.js';

const LAYOUTS: Readonly<Record<LayoutId, KeyboardLayout>> = {
  'en-qwerty': EN_QWERTY,
  'ru-jcuken': RU_JCUKEN,
};

/**
 * Отличия раскладок по операционным системам.
 *
 * Раскладка без отличий не описывается вовсе: пустая запись читалась бы как
 * «отличия есть, но их забыли перечислить». English QWERTY одинакова на всех
 * трёх системах в пределах буквенно-цифрового блока.
 */
const OVERRIDES: Readonly<Partial<Record<LayoutId, PlatformOverrides>>> = {
  'ru-jcuken': RU_JCUKEN_OVERRIDES,
};

/**
 * Собранные варианты раскладок по системам.
 *
 * Вариант собирается один раз и дальше живёт как единственный объект: по нему
 * же строится индекс символов, а сравнение раскладок по ссылке остаётся
 * осмысленным.
 */
const VARIANTS = new Map<string, KeyboardLayout>();

/** Индекс символов строится на объект раскладки: у вариантов один и тот же id. */
const STROKE_INDEX = new Map<KeyboardLayout, Map<string, KeyStroke>>();

function indexFor(layout: KeyboardLayout): Map<string, KeyStroke> {
  const cached = STROKE_INDEX.get(layout);
  if (cached !== undefined) return cached;

  const index = new Map<string, KeyStroke>();
  for (const key of layout.keys) {
    if (key.base !== '' && !index.has(key.base)) {
      index.set(key.base, { key, requiresShift: false });
    }
    if (key.shifted !== '' && !index.has(key.shifted)) {
      index.set(key.shifted, { key, requiresShift: true });
    }
  }
  STROKE_INDEX.set(layout, index);
  return index;
}

/**
 * Применяет отличия системы к клавишам раскладки.
 *
 * Меняются только символы: палец, ряд и колонка остаются, потому что
 * физическая клавиатура от системы не зависит. Место клавиши задаётся рядом и
 * колонкой — так отличие остаётся отличием клавиши, а не строкой символов.
 *
 * @throws {Error} если отличие указывает на клавишу, которой в раскладке нет:
 *   молчаливый пропуск оставил бы пользователя с чужой подсказкой.
 */
function withOverrides(layout: KeyboardLayout, overrides: readonly KeyOverride[]): KeyboardLayout {
  const keys = layout.keys.map((key) => {
    const override = overrides.find((item) => item.row === key.row && item.column === key.column);
    return override === undefined
      ? key
      : { ...key, base: override.base, shifted: override.shifted };
  });

  for (const override of overrides) {
    const exists = layout.keys.some(
      (key) => key.row === override.row && key.column === override.column,
    );
    if (!exists) {
      throw new Error(
        `Раскладка ${layout.id}: отличие указывает на клавишу ${override.row}:${override.column}, которой нет`,
      );
    }
  }

  return { ...layout, keys };
}

/**
 * Возвращает раскладку по идентификатору и операционной системе.
 *
 * Система — часть раскладки, а не её оформления: Apple расставляет знаки
 * русской раскладки иначе, и подсказка клавиши без учёта системы верна только
 * для одной из них. Идентификатор при этом остаётся прежним: клавиша остаётся
 * собой, меняется её место, а не единица обучения (ТЗ FR-EN-43).
 */
export function getLayout(id: LayoutId, platform: OsPlatform = DEFAULT_PLATFORM): KeyboardLayout {
  const base = LAYOUTS[id];
  const overrides = OVERRIDES[id]?.[platform];
  if (overrides === undefined || overrides.length === 0) return base;

  const key = `${id}:${platform}`;
  const cached = VARIANTS.get(key);
  if (cached !== undefined) return cached;

  const variant = withOverrides({ ...base, platform }, overrides);
  VARIANTS.set(key, variant);
  return variant;
}

/** Все поддерживаемые раскладки. */
export function allLayouts(): readonly KeyboardLayout[] {
  return Object.values(LAYOUTS);
}

/** Как ввести символ в этой раскладке, либо null, если символа в ней нет. */
export function strokeForCharacter(layout: KeyboardLayout, character: string): KeyStroke | null {
  return indexFor(layout).get(character) ?? null;
}

/** Палец, которым набирается символ, либо null. */
export function fingerForCharacter(layout: KeyboardLayout, character: string): Finger | null {
  return strokeForCharacter(layout, character)?.key.finger ?? null;
}

/** Клавиша, дающая символ, либо null. */
export function keyForCharacter(layout: KeyboardLayout, character: string): KeyDefinition | null {
  return strokeForCharacter(layout, character)?.key ?? null;
}

/**
 * Раскладка, в которой набирается символ.
 *
 * Нужна там, где текст смешанный: в упражнении с русскими и латинскими
 * фрагментами раскладка сессии описывает только половину символов, и
 * ошибку во второй половине не с чем сравнивать.
 *
 * Предпочтение отдаётся переданной раскладке: цифры, пробел и точка есть в
 * обеих, и подменять раскладку там, где она подходит, значит терять близость
 * клавиш.
 *
 * @param character символ;
 * @param preferred раскладка, которая главнее при совпадении.
 * @returns раскладку, поддерживающую символ, либо `preferred`, если такой нет.
 */
export function layoutForCharacter(character: string, preferred: KeyboardLayout): KeyboardLayout {
  if (supportsCharacter(preferred, character)) return preferred;
  return allLayouts().find((layout) => supportsCharacter(layout, character)) ?? preferred;
}

/** Присутствует ли символ в раскладке. */
export function supportsCharacter(layout: KeyboardLayout, character: string): boolean {
  return indexFor(layout).has(character);
}
